import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Drawer,
  Typography,
  TextInput,
  Button,
  IconButton,
  Loader,
  color,
  Card,
  Overlay,
  TextArea
} from '@kistech/kis-ui';
import { Box as MuiBox, Typography as MuiTypography, Grid } from '@mui/material';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import {
  actions as kioskHardwareActions,
  selectors as kioskHardwareSelectors,
  KioskHardware
} from 'store/kioskHardware';
import { useStyles } from '../../styled';
import { getKioskHardwareInitialForm } from '../../utils';
import { MappingEditDrawerProps } from '../../types';
import { INVALID, VALID } from '../../constants'; // needs to be changed

export const MappingEditDrawer = (props: MappingEditDrawerProps) => {
  const { isOpen, onClose, configData, configItem, kiosk } = props;

  const [updateKioskHardware, resetKioskHardwareError] = useActions([
    kioskHardwareActions.updateKioskHardware,
    kioskHardwareActions.resetError
  ]);

  const { error: kioskHardwareError, pending: kioskHardwarePending } = useSelector(kioskHardwareSelectors.selectAction);

  const [error, setError] = useState<string>('');
  const [apiLoading, setAPILoading] = useState(false);
  const [uiLoading, setUILoading] = useState(false);

  // SETS THE FORM VALUES AND VALIDATION VALUES FOR EACH FORM VALUE (INVALID | VALID)
  const initialForm = getKioskHardwareInitialForm(configData);
  const [form, setForm] = useState(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});

  const classes = useStyles({ showDrawer: isOpen })();

  useEffect(() => {
    if (kioskHardwareError) {
      setError(kioskHardwareError as string);
      setUILoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskHardwareError]);

  useEffect(() => {
    setAPILoading(kioskHardwarePending as boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskHardwarePending]);

  useEffect(() => {
    if (uiLoading && !apiLoading && !error) {
      handleOverlayClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, apiLoading]);

  const onConfirm = () => {
    resetError();
    setUILoading(true);

    const payload: Partial<KioskHardware> = {
      id: configData.id,
      kioskId: form.kioskId,
      hardwareId: form.hardwareId,
      serialCode: form.serialCode ? form.serialCode : '',
      configuration: form.configuration ? JSON.parse(form.configuration as string) : {},
      status: form.status
    };
    updateKioskHardware(payload);
  };

  const handleOverlayClose = () => {
    resetError();
    onClose();
  };

  const resetError = () => {
    if (error) {
      setError('');
      if (kioskHardwareError) {
        resetKioskHardwareError();
      }
    }
  };

  const onInputChange = (
    key: keyof ReturnType<typeof getKioskHardwareInitialForm>,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    resetError();
    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }

    setForm({ ...form, [key]: e.target.value });
  };

  return (
    <>
      {apiLoading && (
        <Overlay>
          <Loader color={color.LIGHT_GRAY} />
        </Overlay>
      )}
      <Drawer isOpen={isOpen} onClose={handleOverlayClose} hasBorderRadius>
        <Grid container className={classes.configEditOverlay}>
          <Card fullHeight>
            <Grid className={classes.configOverlayHeaderBlock}>
              <Grid className={classes.configOverlayHeading}>
                <Flex>
                  <MuiTypography className={classes.configEditHeading} style={{ fontWeight: 600 }}>
                    {kiosk.name}
                  </MuiTypography>
                </Flex>
              </Grid>
              <Grid className={classes.configOverlayCloseIcon}>
                <IconButton
                  name="exit_circle"
                  appearance="transparent"
                  size={40}
                  onClick={handleOverlayClose}
                  data-testid="kiosk-restock-x"
                />
              </Grid>
            </Grid>
            <Box padding="2 6" fullWidth bg="light-gray">
              <Typography weight={600} size={16}>
                Kiosk Hardware
              </Typography>
            </Box>
            <Card.Divider color={color.BORDER} />
            <MuiBox className={classes.configContentBox}>
              <Flex fullWidth direction="column" margin="6 32">
                <Typography size={16} weight={600} style={{ padding: '0 0 0 24px' }}>
                  Hardware
                </Typography>
                <Box width={34} />
                <Typography size={16} style={{ padding: '0 0 0 24px' }}>
                  {`${configItem.name} (${configItem.type})`}
                </Typography>
              </Flex>
              <MuiBox className={classes.createClientName}>
                <TextInput
                  label="Serial Code"
                  size="medium"
                  fullWidth
                  placeholder="Serial Code"
                  value={form.serialCode ? form.serialCode : ''}
                  onChange={(e) => onInputChange('serialCode', e)}
                  isError={validation.serialCode === INVALID}
                />
              </MuiBox>
              <MuiBox className={classes.createClientName} style={{ height: '275px' }}>
                <TextArea
                  label="Configuration"
                  size="medium"
                  fullWidth
                  placeholder="Configuration"
                  value={form.configuration ? form.configuration : ''}
                  onChange={(e) => onInputChange('configuration', e)}
                  isError={validation.configuration === INVALID}
                />
              </MuiBox>
              <Box margin="1 0 0">
                <Typography color={color.RED} size={14}>
                  {error}
                </Typography>
              </Box>
              {/* button */}
              <MuiBox className={classes.saveButtonBox}>
                <Button size="small" appearance="primary" onClick={onConfirm} className={classes.saveButton}>
                  Save
                </Button>
              </MuiBox>
            </MuiBox>
          </Card>
        </Grid>
      </Drawer>
    </>
  );
};
